"use client";

import { useEffect, useRef, useState } from "react";

/* ── Types ──────────────────────────────────────────────────────────────── */

export interface ReviseSectionProps {
  /** Section title — shown in the popover's eyebrow target, in the textarea
   *  placeholder, and as the rendered <h2> when `withHeader` is true. */
  title: string;
  /** Stable identifier passed back to `onAddRevision` so the caller knows which
   *  section the revision targets. */
  sectionId: string;
  /** Invoked once when the user submits a non-empty draft (clicks "Add
   *  revision" or presses ⌘/Ctrl+Enter). The parent wires this to its own
   *  state setter to capture the revision. */
  onAddRevision: (sectionId: string, text: string) => void;
  /** Layout mode. `false` (default) overlays just the pencil + popover in the
   *  top-right corner of a `relative` container, over the section's own header.
   *  `true` renders the wrapper's own `<h2>{title}</h2>` + pencil header for
   *  title-less sections. */
  withHeader?: boolean;
  /** When false, render the section + children with NO edit affordance. Keeps
   *  the `<section id>` for scroll-spy. Defaults to true. */
  editable?: boolean;
  /** When true, the pencil still renders but is disabled — submitting a new
   *  revision is suppressed. Mirrors the side-rail `RevisePanel` lock so a
   *  resolving revision (or in-flight regen) can't be bypassed from a section
   *  header. Defaults to false. */
  locked?: boolean;
  /** Tooltip shown on the disabled pencil explaining why it's locked. */
  lockedReason?: string;
  /** Accessible name for the section landmark (applied as `aria-label` on the
   *  rendered `<section>`). Use when the section's children don't render their
   *  own visible heading. */
  ariaLabel?: string;
  children: React.ReactNode;
}

/* ── Injected styles ─────────────────────────────────────────────────────── */
// The 140ms pop keyframe + the dark-mode submit override. Atlas does dark mode
// by swapping CSS-variable values at `html[data-theme="dark"], .dark` and does
// NOT wire a Tailwind `dark:` variant, so the submit override is scoped to the
// `.revise-section-submit` class under both selectors here.
const POP_STYLES = `
@keyframes revise-section-pop {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
html[data-theme="dark"] .revise-section-submit,
.dark .revise-section-submit {
  background: var(--steel-80);
  border-color: var(--steel-50);
}
`;

/* ── Icon ────────────────────────────────────────────────────────────────── */

function PencilIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={true}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

/* ── Component ───────────────────────────────────────────────────────────── */

export function ReviseSection({
  title,
  sectionId,
  onAddRevision,
  withHeader = false,
  editable = true,
  locked = false,
  lockedReason = "A revision is already resolving. You can add another once it finishes.",
  ariaLabel,
  children,
}: ReviseSectionProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Autofocus the textarea when the popover opens.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Close the popover the moment the section locks (mirrors RevisePanel) so a
  // mid-compose revision can't be submitted once a resolve is in flight.
  useEffect(() => {
    if (locked) setOpen(false);
  }, [locked]);

  // Close on click outside the pencil + popover wrapper.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const el = wrapRef.current;
      if (el && !el.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!editable) {
    return (
      <section id={sectionId} aria-label={ariaLabel} className="relative">
        {withHeader ? (
          <header className="flex items-center justify-between gap-3">
            <h2 className="m-0 text-base font-semibold text-[var(--fg-primary)]">
              {title}
            </h2>
          </header>
        ) : null}
        {children}
      </section>
    );
  }

  function submit() {
    const text = draft.trim();
    if (!text || locked) return;
    onAddRevision(sectionId, text);
    setDraft("");
    setOpen(false);
  }

  function cancel() {
    setDraft("");
    setOpen(false);
  }

  const editControl = (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => {
          if (!locked) setOpen((v) => !v);
        }}
        disabled={locked}
        aria-label={`Suggest a revision to ${title}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={locked ? lockedReason : "Suggest revision"}
        className={[
          "inline-flex h-7 w-7 items-center justify-center rounded-[3px]",
          "transition-colors duration-100",
          locked
            ? "cursor-not-allowed text-[var(--fg-muted)] opacity-50"
            : [
                "cursor-pointer text-[var(--fg-muted)]",
                "hover:bg-[var(--bg-canvas)] hover:text-[var(--fg-primary)]",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color-mix(in_oklab,var(--fg-primary)_35%,transparent)]",
                "aria-expanded:bg-[var(--bg-canvas)] aria-expanded:text-[var(--fg-primary)]",
              ].join(" "),
        ].join(" ")}
      >
        <PencilIcon />
      </button>

      {open && !locked && (
        <div
          role="dialog"
          aria-label={`Suggest a revision to ${title}`}
          className={[
            "absolute top-[calc(100%+6px)] right-0 z-50 w-[360px] max-w-[calc(100vw-32px)] p-3",
            "bg-[var(--bg-white)] border border-[var(--border-default)] rounded-[4px]",
            "shadow-[0_12px_32px_rgba(10,16,32,0.14),0_2px_6px_rgba(10,16,32,0.06)]",
            "flex flex-col gap-2",
            "animate-[revise-section-pop_140ms_ease-out] motion-reduce:animate-none",
          ].join(" ")}
        >
          <div className="flex items-baseline gap-2 border-b border-[var(--border-subtle)] pb-1.5">
            <span className="shrink-0 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--fg-muted)]">
              Suggest revision
            </span>
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[11.5px] font-medium text-[var(--fg-secondary)]">
              {title}
            </span>
          </div>

          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`Describe what to change in ${title.toLowerCase()}…`}
            className={[
              "box-border min-h-[80px] w-full resize-y rounded-[3px] px-2.5 py-2",
              "border border-[var(--border-subtle)] bg-[var(--bg-white)] outline-none",
              "font-body text-[13px] leading-[1.5] text-[var(--fg-primary)]",
              "focus:border-[var(--fg-primary)]",
            ].join(" ")}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                if (draft.trim()) submit();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              }
            }}
          />

          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="mr-auto whitespace-nowrap font-mono text-[10px] text-[var(--fg-muted)]">
              ⌘ + Enter to queue
            </span>
            <button
              type="button"
              onClick={cancel}
              className={[
                "cursor-pointer whitespace-nowrap rounded-[3px] px-3 py-1.5 text-[12px] font-medium",
                "border border-[var(--border-default)] bg-[var(--bg-white)] text-[var(--fg-secondary)]",
                "transition-colors duration-100 hover:bg-[var(--bg-canvas)] hover:text-[var(--fg-primary)]",
              ].join(" ")}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!draft.trim()}
              className={[
                "revise-section-submit cursor-pointer whitespace-nowrap rounded-[3px] px-3 py-1.5 text-[12px] font-medium",
                "border border-[var(--fg-brand)] bg-[var(--fg-brand)] text-[var(--fg-inverse)]",
                "transition-colors duration-100 hover:bg-[color-mix(in_oklab,var(--brand-navy)_90%,white)]",
                "disabled:cursor-not-allowed disabled:opacity-50",
              ].join(" ")}
            >
              Add revision
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <section id={sectionId} aria-label={ariaLabel} className="relative">
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: self-contained keyframe + dark-mode override, no user input */}
      <style dangerouslySetInnerHTML={{ __html: POP_STYLES }} />

      {withHeader ? (
        <>
          <header className="flex items-center justify-between gap-3">
            <h2 className="m-0 text-base font-semibold text-[var(--fg-primary)]">
              {title}
            </h2>
            {editControl}
          </header>
          <div>{children}</div>
        </>
      ) : (
        <>
          <div className="absolute right-0 top-0 z-10">{editControl}</div>
          {children}
        </>
      )}
    </section>
  );
}
